import { HomeHero } from "@/components/HomeHero";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, RefreshCw, Star, BadgeCheck, LayoutTemplate, Radio, Store, Gift, PawPrint, Frame, Sparkles } from "lucide-react";
import api, { img } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { ProductCard } from "@/components/ProductCard";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { AdsMarquee } from "@/components/AdsMarquee";
import { FirstVisitWelcome } from "@/components/FirstVisitWelcome";
import { DocsSpotlight } from "@/components/DocsSpotlight";
import { TemplatesSpotlight } from "@/components/TemplatesSpotlight";
import { BodyGoals } from "@/components/BodyGoals";
import { currentSeason, SEASONS } from "@/lib/season";

const HERO_SLIDES = [
  { src: "/hero/hero1.jpg", alt: "Shoppers in a London arcade" },
  { src: "/hero/hero2.jpg", alt: "UK artisan market at night" },
  { src: "/hero/hero3.jpg", alt: "Curated premium products" },
];

export default function Home() {
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [live, setLive] = useState([]);
  const [recs, setRecs] = useState({ personalized: false, items: [] });
  const [farm, setFarm] = useState([]);
  const [kids, setKids] = useState([]);
  const [sp] = useSearchParams();
  const seasonKey = SEASONS[sp.get("season")] ? sp.get("season") : currentSeason();
  const season = seasonKey ? SEASONS[seasonKey] : null;
  const slides = season ? [{ src: season.hero, alt: season.label }, ...HERO_SLIDES.slice(0, 2)] : HERO_SLIDES;

  useSeo({
    title: "MRQK UK — Digital Services Marketplace",
    description: "Shop fashion, electronics, fresh groceries, digital goods & music from trusted UK sellers — or open your own store with AI tools and automatic payouts.",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "Organization", name: "MRQK UK", alternateName: ["MRQK", "MRQK UK Digital Services Marketplace"], url: window.location.origin, logo: `${window.location.origin}/logo.png`,
          address: { "@type": "PostalAddress", streetAddress: "3rd Floor, Old Stock Exchange, St Nicholas Street", addressLocality: "Bristol", addressRegion: "Avon", postalCode: "BS1 1TG", addressCountry: "GB" } },
        {
          "@type": "WebSite", name: "MRQK UK", url: window.location.origin,
          potentialAction: {
            "@type": "SearchAction",
            target: { "@type": "EntryPoint", urlTemplate: `${window.location.origin}/products?search={search_term_string}` },
            "query-input": "required name=search_term_string",
          },
        },
      ],
    },
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/products?featured=true&limit=8").then((r) => setFeatured(r.data.items || [])).catch(() => {});
    api.get("/products?sort=newest&limit=8").then((r) => setLatest(r.data.items || [])).catch(() => {});
    api.get("/reviews/featured").then((r) => setReviews(r.data.slice(0, 6))).catch(() => {});
    api.get("/live").then((r) => setLive(r.data)).catch(() => {});
    api.get("/recommendations?limit=4").then((r) => setRecs(r.data)).catch(() => {});
    api.get("/products?category=Groceries&limit=4").then((r) => setFarm(r.data.items || [])).catch(() => {});
    api.get("/products?search=MRQK%20Kids&limit=4").then((r) => setKids(r.data.items || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" data-season={seasonKey || "none"} className="bg-[#0E1216] text-neutral-200">
      <FirstVisitWelcome />
      {/* LIVE STRIP */}
      {live.length > 0 && (
        <Link to="/live" className="block bg-[#E63946] text-white" data-testid="home-live-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1.5 font-bold uppercase tracking-wider"><Radio className="w-4 h-4 animate-pulse" />Live now</span>
            <span className="truncate">{live[0].vendor_name}: {live[0].title}</span>
            <span className="ml-auto flex items-center gap-1 whitespace-nowrap">Watch & shop <ArrowRight className="w-4 h-4" /></span>
          </div>
        </Link>
      )}

      {/* CINEMATIC HERO — slideshow + owner-selectable layout (preview any with ?hero=a|b|c) */}
      <HomeHero slides={slides} season={season} featured={featured} cats={cats} requested={sp.get("hero")} />

      {/* TRUST STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-2 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 relative z-10">
        {[[Truck, "Free UK delivery", "On orders over £50"], [ShieldCheck, "Secure checkout", "Card, Apple & Google Pay, Klarna, PayPal"], [RefreshCw, "Easy returns", "30-day UK returns policy"]].map(([Icon, t, s], i) => (
          <div key={i} className="bg-[#12171D] p-5 flex items-center gap-3">
            <Icon className="w-6 h-6 text-[#6EA8FF]" strokeWidth={1.5} />
            <div><p className="font-medium text-sm text-neutral-100">{t}</p><p className="text-xs text-neutral-500">{s}</p></div>
          </div>
        ))}
      </section>

      {/* SPONSORED ADS */}
      <AdsMarquee />

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
        <div className="flex items-end justify-between mb-8">
          <div><span className="label-caps text-neutral-500">Discover</span><h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mt-1 text-white">Browse by category</h2></div>
          <Link to="/products" className="text-sm text-[#6EA8FF] hover:underline">View all →</Link>
        </div>
        <div className={`grid gap-4 md:gap-6 ${cats.length > 4 ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-5" : "grid-cols-2 md:grid-cols-4"}`}>
          {cats.map((c) => (
            <Link key={c.id} to={`/products?category=${encodeURIComponent(c.name)}`} data-testid={`home-cat-${c.slug}`} className="group border border-white/10 bg-[#151A21] hover:border-white/30 transition-all">
              <div className="aspect-[4/3] overflow-hidden bg-[#0E1216]"><img src={img(c.image_url, 480)} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="p-4 flex items-center justify-between"><span className="font-medium text-neutral-100">{c.name}</span><ArrowRight className="w-4 h-4 text-neutral-400 group-hover:translate-x-1 group-hover:text-white transition-all" /></div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <div className="mb-8"><span className="label-caps text-neutral-500">Editor's picks</span><h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mt-1 text-white">Featured products</h2></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">{featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} dark />)}</div>
        </section>
      )}

      {/* RECOMMENDATIONS */}
      {recs.items.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20" data-testid="home-recommendations">
          <div className="mb-8">
            <span className="label-caps text-neutral-500">{recs.personalized ? "Just for you" : "Handpicked"}</span>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mt-1 text-white">{recs.personalized ? "Recommended for you" : "Trending picks"}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">{recs.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} dark />)}</div>
        </section>
      )}

      {/* FRESH FROM THE FARM */}
      {farm.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20" data-testid="home-farm-spotlight">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="label-caps text-[#7CB342]">Straight from the farm gate</span>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mt-1 text-white">Fresh from the farm</h2>
              <p className="text-neutral-400 text-sm mt-2 max-w-xl">Groceries, produce and pantry staples from the UK's independent farm shops and food makers — picked, packed and sent by the people who grow it.</p>
            </div>
            <Link to="/products?category=Groceries" className="text-sm text-[#7CB342] hover:underline whitespace-nowrap" data-testid="home-farm-viewall">Shop all →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">{farm.map((p, i) => <ProductCard key={p.id} product={p} index={i} dark />)}</div>
        </section>
      )}

      {/* KIDS CORNER */}
      {kids.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20" data-testid="home-kids-corner">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="label-caps text-[#FFC107]">Newborns to teens · instant downloads</span>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mt-1 text-white">Kids' corner</h2>
              <p className="text-neutral-400 text-sm mt-2 max-w-xl">Printable milestone cards, colouring packs, chore charts, party kits and revision planners — download in seconds, print at home, made for every age.</p>
            </div>
            <Link to={`/products?search=${encodeURIComponent("MRQK Kids")}`} className="text-sm text-[#FFC107] hover:underline whitespace-nowrap" data-testid="home-kids-viewall">See the range →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">{kids.map((p, i) => <ProductCard key={p.id} product={p} index={i} dark />)}</div>
        </section>
      )}

      {/* GIFTS: VOUCHERS + PET PORTRAITS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20" data-testid="home-gifts">
        <div className="mb-8"><span className="label-caps text-neutral-500">{season ? season.giftLabel : "Gifts in seconds"}</span><h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mt-1 text-white">{season ? season.giftHeading : "Give something special"}</h2></div>
        {season && (
          <Link to="/cards" className="block mb-6 border border-[#FFC107]/40 bg-[#FFC107]/[0.06] px-5 py-3.5 text-sm text-neutral-200 hover:border-[#FFC107] transition-colors" data-testid="home-seasonal-strip">
            {season.strip} <span className="text-[#FFC107] underline ml-1">Browse cards →</span>
          </Link>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Link to="/gift-vouchers" data-testid="home-vouchers-card" className="group border border-white/10 bg-[#151A21] hover:border-[#FFC107]/50 transition-colors overflow-hidden">
            <div className="aspect-[16/9] overflow-hidden bg-[#0E1216]"><img src={img("https://static.prod-images.emergentagent.com/jobs/ba111a28-58ed-46e3-b069-6bf0d797ed65/images/829eab529f62c789c8e8b124375a3a4ac057c7f76483b2b3b29e9ed4c5c8eba7.jpeg", 800)} alt="MRQK UK gift voucher" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>
            <div className="p-6 flex items-start gap-4">
              <div className="w-11 h-11 bg-[#FFC107] flex items-center justify-center shrink-0"><Gift className="w-5 h-5 text-black" strokeWidth={1.5} /></div>
              <div className="flex-1">
                <h3 className="font-heading text-xl text-white mb-1">Gift vouchers, delivered instantly</h3>
                <p className="text-sm text-neutral-400">From £10 — emailed straight to them, spendable across every store on MRQK UK. Valid 12 months.</p>
              </div>
              <ArrowRight className="w-5 h-5 text-neutral-500 group-hover:text-white group-hover:translate-x-1 transition-all mt-2 shrink-0" />
            </div>
          </Link>
          <Link to="/pet-portraits" data-testid="home-pets-card" className="group border border-white/10 bg-[#151A21] hover:border-[#6EA8FF]/60 transition-colors overflow-hidden">
            <div className="aspect-[16/9] overflow-hidden bg-[#0E1216]"><img src={img("https://static.prod-images.emergentagent.com/jobs/ba111a28-58ed-46e3-b069-6bf0d797ed65/images/0531e72eacf6f4698c268da45c9bb1fb322eff301bc7545ae620349f7ab2b3ff.jpeg", 800)} alt="Custom AI royal pet portrait" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>
            <div className="p-6 flex items-start gap-4">
              <div className="w-11 h-11 bg-[#0047AB] flex items-center justify-center shrink-0"><PawPrint className="w-5 h-5 text-white" strokeWidth={1.5} /></div>
              <div className="flex-1">
                <h3 className="font-heading text-xl text-white mb-1">Custom AI pet portraits — £12.99</h3>
                <p className="text-sm text-neutral-400">Their photo becomes royal art in minutes. Print-ready downloads up to A3, with free re-paints.</p>
              </div>
              <ArrowRight className="w-5 h-5 text-neutral-500 group-hover:text-white group-hover:translate-x-1 transition-all mt-2 shrink-0" />
            </div>
          </Link>
          <Link to="/wall-art" data-testid="home-wall-art-card" className="group border border-white/10 bg-[#151A21] hover:border-[#F59E0B]/60 transition-colors overflow-hidden">
            <div className="aspect-[16/9] overflow-hidden bg-[#0E1216]"><img src={img("https://static.prod-images.emergentagent.com/jobs/ba111a28-58ed-46e3-b069-6bf0d797ed65/images/e378b5ff8192a71ed9173e2a8a503f82cbb627aace0d1b42975121482b06c5bd.jpeg", 800)} alt="MRQK printable wall art — Cornish coast" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>
            <div className="p-6 flex items-start gap-4">
              <div className="w-11 h-11 bg-[#B45309] flex items-center justify-center shrink-0"><Frame className="w-5 h-5 text-white" strokeWidth={1.5} /></div>
              <div className="flex-1">
                <h3 className="font-heading text-xl text-white mb-1">Printable wall art — from £3.99</h3>
                <p className="text-sm text-neutral-400">Original UK prints in five frame sizes at 300 DPI, with a one-page guide to printing at home or on the high street.</p>
              </div>
              <ArrowRight className="w-5 h-5 text-neutral-500 group-hover:text-white group-hover:translate-x-1 transition-all mt-2 shrink-0" />
            </div>
          </Link>
          <Link to="/brand-studio" data-testid="home-brand-studio-card" className="group border border-white/10 bg-[#151A21] hover:border-[#6EA8FF]/60 transition-colors overflow-hidden">
            <div className="aspect-[16/9] overflow-hidden bg-[#0E1216]"><img src={img("https://static.prod-images.emergentagent.com/jobs/ba111a28-58ed-46e3-b069-6bf0d797ed65/images/e3dcf7e3d6922498e4a590cd540f785f93fa89e4b3fa3da3444a5078bf44353a.jpeg", 800)} alt="MRQK Brand Studio — AI logo and brand kit" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>
            <div className="p-6 flex items-start gap-4">
              <div className="w-11 h-11 bg-[#0047AB] flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} /></div>
              <div className="flex-1">
                <h3 className="font-heading text-xl text-white mb-1">Brand Studio — AI logos, thumbnails &amp; kits</h3>
                <p className="text-sm text-neutral-400">Buy a package, fill in a two-minute brief, download finished files for every platform. Free redo included.</p>
              </div>
              <ArrowRight className="w-5 h-5 text-neutral-500 group-hover:text-white group-hover:translate-x-1 transition-all mt-2 shrink-0" />
            </div>
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {reviews.length > 0 && (
        <section className="border-y border-white/10 bg-white/[0.02] mt-20 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="mb-10"><span className="label-caps text-neutral-500">Trusted by UK shoppers</span><h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mt-1 text-white">What customers say</h2></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {reviews.map((r) => (
                <div key={r.id} className="border border-white/10 p-6 bg-[#151A21]" data-testid={`home-review-${r.id}`}>
                  <div className="flex gap-0.5 mb-3">{[1,2,3,4,5].map((s) => <Star key={s} className={`w-4 h-4 ${s <= r.rating ? "fill-[#FFC107] text-[#FFC107]" : "text-neutral-700"}`} />)}</div>
                  <p className="text-neutral-200 leading-relaxed mb-4">“{r.comment}”</p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-neutral-100">{r.user_name}</span>
                    {r.verified && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#5fd39e]"><BadgeCheck className="w-3 h-3" />Verified</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TEMPLATES CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
        <div className="border border-white/10 bg-[#151A21] p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
          <div className="w-16 h-16 bg-[#0047AB] flex items-center justify-center shrink-0"><LayoutTemplate className="w-8 h-8 text-white" strokeWidth={1.5} /></div>
          <div className="flex-1 text-center md:text-left">
            <span className="label-caps text-[#6EA8FF]">For vendors · Under Digital</span>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium mt-1 mb-2 text-white">Launch your store with a designer template</h2>
            <p className="text-neutral-400">43 professionally-designed themes across Fashion, Electronics, Groceries, Digital, Streaming & Music. Apply instantly or download the starter kit.</p>
          </div>
          <Link to="/templates" data-testid="home-store-templates-cta" className="bg-[#0047AB] text-white px-6 py-3 font-medium hover:bg-[#003380] transition-colors flex items-center gap-2 whitespace-nowrap">Browse templates <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* WORKPLACE DOCUMENTS SPOTLIGHT */}
      <DocsSpotlight />
      <TemplatesSpotlight />

      {/* BODY GOALS — MRQK FIT */}
      <BodyGoals />

      {/* LATEST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
        <div className="flex items-end justify-between mb-8">
          <div><span className="label-caps text-neutral-500">New in</span><h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mt-1 text-white">Latest arrivals</h2></div>
          <Link to="/products" className="text-sm text-[#6EA8FF] hover:underline">Shop all →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">{latest.map((p, i) => <ProductCard key={p.id} product={p} index={i} dark />)}</div>
      </section>

      {/* NEWSLETTER */}
      <div className="pb-4"><NewsletterSignup variant="hero" /></div>
    </div>
  );
}
